import { initAxios } from 'utils/axiosHelper';
import {
  IComplianceHealthStats,
  IEntityDashboardStats,
  IEntityQualityMetrics,
  IGroupStats,
  IHighRiskEntityWeeklyStats,
  IProductDashboardStats,
} from './stats';

export const getGroupStats = async (
  groupId: string,
): Promise<IGroupStats | null> => {
  try {
    const instance = initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/stats/${groupId}`);

    const groupStats: IGroupStats = resp.data?.data;
    return groupStats;
  } catch (err) {
    console.error('Error fetching group stats:', err);
    return null;
  }
};

export const getGroupEntityDashboardStats = async (
  groupId: string,
): Promise<IEntityDashboardStats | null> => {
  try {
    const instance = initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/stats/entities/group/${groupId}`);

    return resp.data?.data ?? null;
  } catch (err) {
    console.error('Error fetching group entity dashboard stats:', err);
    return null;
  }
};

export const getProductIssuesByCountry = async (
  groupId: string,
): Promise<Record<string, number>[] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/products/group/${groupId}/issues-by-country`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching product issues by country:', err);
    return null;
  }
};

export const getProductIssuesByPriority = async (
  groupId: string,
  page?: number,
  pageSize?: number,
): Promise<IProductDashboardStats['IssuesByPriority'] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const queryParams = new URLSearchParams({
      page: page?.toString() ?? '',
      pageSize: pageSize?.toString() ?? '',
    });

    const resp = await instance.get(
      `/bite-db/stats/products/group/${groupId}/issues-by-priority`,
      {
        params: queryParams,
      },
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching product issues by priority:', err);
    return null;
  }
};

export const getProductIssuesByType = async (
  groupId: string,
): Promise<Record<string, number>[] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/products/group/${groupId}/issues-by-type`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching product issues by type:', err);
    return null;
  }
};

export const getProductRecentActivity = async (
  groupId: string,
): Promise<IProductDashboardStats['RecentActivity'] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/products/group/${groupId}/recent-activity`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching product recent activity:', err);
    return null;
  }
};


export const getGroupRecentActivityStats = async (
  groupId: string,
): Promise<any[] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/recent-sctivity/group/${groupId}`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching group recent activity stats:', err);
    return null;
  }
};

export const getProductStats = async (
  groupId: string,
  priorityPage?: number,
  priorityPageSize?: number,
): Promise<IProductDashboardStats | null> => {
  try {
    const [IssuesByCountry, IssuesByPriority, IssuesByType, RecentActivity] =
      await Promise.all([
        getProductIssuesByCountry(groupId),
        getProductIssuesByPriority(
          groupId,
          priorityPage ?? 1,
          priorityPageSize ?? 10,
        ),
        getProductIssuesByType(groupId),
        getProductRecentActivity(groupId),
      ]);

    return {
      IssuesByCountry: IssuesByCountry ?? [],
      IssuesByPriority: IssuesByPriority?.rows ?? [],
      IssuesByType: IssuesByType ?? [],
      RecentActivity: RecentActivity ?? [],
      IssuesByPriorityCount: IssuesByPriority?.count ?? 0,
    };
  } catch (err) {
    console.error('Error fetching group stats:', err);
    return null;
  }
};

export const getGroupProductDbStats = async (
  groupId: string,
): Promise<Partial<IGroupStats> | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/stats/${groupId}/products`);

    return resp.data?.data ?? null;
  } catch (err) {
    console.error('Error fetching group product database stats:', err);
    return null;
  }
};

export const getDatabaseStats = async (
  databaseId: string,
): Promise<IGroupStats | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/stats/database/${databaseId}`);

    const databaseStats: IGroupStats = resp.data?.data;
    return databaseStats;
  } catch (err) {
    console.error('Error fetching database stats:', err);
    return null;
  }
};

const BATCH_LIMIT = 200;

export const getDatabaseStatsBatch = async (
  databaseIds: string[],
): Promise<Record<string, IGroupStats>> => {
  if (databaseIds.length === 0) return {};
  try {
    const instance = await initAxios();
    if (!instance) return {};
    const chunks: string[][] = [];
    for (let i = 0; i < databaseIds.length; i += BATCH_LIMIT) {
      chunks.push(databaseIds.slice(i, i + BATCH_LIMIT));
    }
    const results = await Promise.all(
      chunks.map((chunk) =>
        instance
          .get(`/bite-db/stats/databases/batch?ids=${chunk.join(',')}`)
          .then((r) => r.data?.data ?? {})
          .catch(() => ({})),
      ),
    );
    return Object.assign({}, ...results);
  } catch (err) {
    console.error('Error fetching batch database stats:', err);
    return {};
  }
};

export const getEntityQualityMetrics = async (
  groupId: string,
): Promise<IEntityQualityMetrics | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(`/bite-db/stats/entity-quality/${groupId}`);
    const metrics: IEntityQualityMetrics = resp.data?.data;
    return metrics;
  } catch (err) {
    console.error('Error fetching entity quality metrics:', err);
    return null;
  }
};

export const getHighRiskEntitiesThisWeek = async (
  groupId: string,
): Promise<IHighRiskEntityWeeklyStats | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/high-risk-entities-weekly/${groupId}`,
    );
    return (
      resp.data?.data ?? {
        total_high_risk_entities: 0,
        high_risk_entities_this_week: 0,
      }
    );
  } catch (err) {
    console.error('Error fetching weekly high risk entities:', err);
    return null;
  }
};

export const getComplianceHealth = async (
  groupId: string,
): Promise<IComplianceHealthStats | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/compliance-health/${groupId}`,
    );
    return (
      resp.data?.data ?? {
        current_score: 0,
        last_month_score: 0,
        change_pct_points: 0,
      }
    );
  } catch (err) {
    console.error('Error fetching compliance health:', err);
    return null;
  }
};

export const getDatabaseProductIssuesByCountry = async (
  databaseId: string,
): Promise<Record<string, number>[] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/products/${databaseId}/issues-by-country`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching database product issues by country:', err);
    return null;
  }
};

export const getDatabaseProductIssuesByPriority = async (
  databaseId: string,
  page?: number,
  pageSize?: number,
): Promise<any | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const queryParams = new URLSearchParams({
      page: page?.toString() ?? '',
      pageSize: pageSize?.toString() ?? '',
    });

    const resp = await instance.get(
      `/bite-db/stats/products/${databaseId}/issues-by-priority`,
      {
        params: queryParams,
      },
    );
    return resp?.data?.data ?? null;
  } catch (err) {
    console.error('Error fetching database product issues by priority:', err);
    return null;
  }
};

export const getDatabaseProductIssuesByType = async (
  databaseId: string,
): Promise<Record<string, number>[] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/products/${databaseId}/issues-by-type`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching database product issues by type:', err);
    return null;
  }
};

export const getDatabaseProductRecentActivity = async (
  databaseId: string,
): Promise<IProductDashboardStats['RecentActivity'] | null> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return null;
    }

    const resp = await instance.get(
      `/bite-db/stats/products/${databaseId}/recent-activity`,
    );
    return resp.data?.data ?? [];
  } catch (err) {
    console.error('Error fetching database product recent activity:', err);
    return null;
  }
};

export const getDatabaseProductStats = async (
  databaseId: string,
  priorityPage?: number,
  priorityPageSize?: number,
): Promise<IProductDashboardStats | null> => {
  try {
    const [IssuesByCountry, IssuesByPriority, IssuesByType, RecentActivity] =
      await Promise.all([
        getDatabaseProductIssuesByCountry(databaseId),
        getDatabaseProductIssuesByPriority(
          databaseId,
          priorityPage ?? 1,
          priorityPageSize ?? 10,
        ),
        getDatabaseProductIssuesByType(databaseId),
        getDatabaseProductRecentActivity(databaseId),
      ]);
    return {
      IssuesByCountry: IssuesByCountry ?? [],
      IssuesByPriority: IssuesByPriority?.rows ?? [],
      IssuesByType: IssuesByType ?? [],
      RecentActivity: RecentActivity ?? [],
      IssuesByPriorityCount: IssuesByPriority?.count ?? 0,
    };
  } catch (err) {
    console.error('Error fetching database product stats:', err);
    return null;
  }
};
